import React, { useState, useEffect } from "react";
import styled from "styled-components";

interface CircleBaseProps {
  className?: string;
  strokeColor?: string;
  strokeWidth?: number;
  innerText?: string;
  legendText?: string;
  percentage: number;
  trailStrokeWidth?: number;
  trailStrokeColor?: string;
  trailSpaced?: boolean;
  speed?: number;
  textColor?: string;
  maxSize?: string;
}

const INITIAL_OFFSET = 25;
const circleConfig = {
  viewBox: "0 0 38 38",
  x: "19",
  y: "19",
  radio: "15.91549430918954",
};

const CircleProgressBarBase = ({
  className,
  strokeColor = "white",
  strokeWidth = 2,
  innerText,
  legendText,
  percentage,
  trailStrokeWidth = 2,
  trailStrokeColor = "rgba(255,255,255,0.5)",
  trailSpaced,
  speed = 2,
  textColor = "white",
}: CircleBaseProps) => {
  const [progressBar, setProgressBar] = useState(0);
  const pace = percentage / speed;
  const updatePercentage = () => {
    setTimeout(() => {
      setProgressBar(progressBar + 1);
    }, pace);
  };

  useEffect(() => {
    if (percentage > 0) updatePercentage();
  }, [percentage]);

  useEffect(() => {
    if (progressBar < percentage) updatePercentage();
  }, [progressBar]);

  return (
    <figure className={className} style={{ margin: 0, padding: 0 }}>
      <svg viewBox={circleConfig.viewBox} height="78" width="78">
        <circle
          className="donut-ring"
          cx={circleConfig.x}
          cy={circleConfig.y}
          r={circleConfig.radio}
          fill="transparent"
          stroke={trailStrokeColor}
          strokeWidth={trailStrokeWidth}
          strokeDasharray={trailSpaced ? 1 : 0}
        />

        <circle
          className="donut-segment"
          cx={circleConfig.x}
          cy={circleConfig.y}
          r={circleConfig.radio}
          fill="transparent"
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          strokeDasharray={`${progressBar} ${100 - progressBar}`}
          strokeDashoffset={INITIAL_OFFSET}
        />

        <g className="chart-text">
          <text x="50%" y="50%" className="chart-number">
            {progressBar}%
          </text>
        </g>
      </svg>
    </figure>
  );
};

const CircleProgressBar = styled(CircleProgressBarBase)`
  max-width: ${(props) => props.maxSize};
  vertical-align: middle;
  .chart-text {
    fill: ${(props) => props.textColor};
    transform: translateY(0.25em);
  }
  .chart-number {
    font-size: 0.6em;
    line-height: 1;
    text-anchor: middle;
    transform: translateY(-0.25em);
  }
  .chart-label {
    font-size: 0.2em;
    text-transform: uppercase;
    text-anchor: middle;
    transform: translateY(0.7em);
  }
  .figure-key [class*="shape-"] {
    margin-right: 8px;
  }
  .figure-key-list {
    list-style: none;
    display: flex;
    justify-content: space-between;
  }
  .figure-key-list li {
    margin: 5px auto;
  }
  .shape-circle {
    display: inline-block;
    vertical-align: middle;
    width: 21px;
    height: 21px;
    border-radius: 50%;
    background-color: ${(props) => props.strokeColor};
    text-transform: capitalize;
  }
  figure {
    margin: 0;
  }
`;

export default CircleProgressBar;
